import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { Chip, CircularProgress, TextField, Typography } from '@mui/material';
import CustomAutocomplete from 'common/components/CustomAutocomplete';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
    root: {
        '& .MuiInputBase-root': {
            background: '#FFFF',
            borderRadius: theme.spacing(2),

            [theme.breakpoints.up('xl')]: {
                height: 'auto',
                borderRadius: theme.spacing(2.5),
            },

            '& > fieldset': {
                borderColor: '#E2E2E2',
                borderRadius: theme.spacing(3),
            },

            '& > input': {
                padding: theme.spacing(0, 1),

                fontSize: '12px',
                fontWeight: '500',
                position: 'relative',
                height: theme.spacing(3),
                '&:focus': {
                    borderRadius: 5,
                },
                [theme.breakpoints.up('xl')]: {
                    fontSize: '14px',
                },
            },
        },
        '& .MuiInputBase-sizeSmall': {
            paddingTop: theme.spacing(0.5) + '!important',
            paddingBottom: theme.spacing(0.5) + '!important',
        },
    },
}));

export const CustomAutocompleteSearchWithPagination = ({
    selectedValues,
    setSelectedValues,
    mapItemToOption,
    keyProp,
    renderOption,
    getOptionLabel,
    isOptionEqualToValue,
    disabled,
    getOptionDisabled,
    fetchAction,
    queryParams,
}) => {
    const { classes } = useStyles();

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [pending, setPending] = useState(false);
    const [wasSearching, setWasSearching] = useState(false);
    const [open, setOpen] = useState(false);
    const handleTagChange = (values) => {
        setSelectedValues([...values]);
    };

    const compareOptionValue = useCallback(
        (option, value) => {
            return typeof isOptionEqualToValue === 'function'
                ? isOptionEqualToValue(option, value)
                : option?.[keyProp || 'id'] === value?.[keyProp || 'id'];
        },
        [keyProp, isOptionEqualToValue],
    );

    const loadData = useCallback(
        (loadPage = 1, shouldReset = false) => {
            setPending(true);
            dispatch(
                fetchAction?.({
                    queryParams: {
                        page: loadPage,
                        limit: 50,
                        ...(search ? { search, name: search } : {}),
                        ...(queryParams || {}),
                        ...(selectedValues?.length
                            ? { priorityIds: selectedValues?.map((item) => item?.[keyProp || 'id']).join(',') }
                            : {}),
                    },
                    onSuccess: (data) => {
                        setHasMore(data?.currentPage < data?.totalPages);
                        setCurrentPage(data?.currentPage);
                        setItems((prevItems) => {
                            // If shouldReset is true, only keep the selected items (if any)
                            // const baseItems = shouldReset
                            //     ? selectedValues?.length
                            //         ? [...selectedValues]
                            //         : []
                            //     : prevItems;

                            const baseItems = prevItems;

                            // Filter out duplicates
                            const newItems = (data?.items || [])
                                .filter((item) => {
                                    const newItem = mapItemToOption(item);
                                    return !baseItems.some((prevItem) => compareOptionValue(prevItem, newItem));
                                })
                                ?.map((item) => mapItemToOption(item));

                            // Create the updated list

                            let updatedItems = [...baseItems, ...newItems];

                            // Needed for include all data in selected values
                            if (selectedValues?.length) {
                                const newSelectedValues = selectedValues?.map((selectedValue) => {
                                    const item = updatedItems.find((item) => compareOptionValue(item, selectedValue));
                                    return item ? { ...item, ...selectedValue } : selectedValue;
                                });
                                setSelectedValues(newSelectedValues);
                            }
                            setPending(false);
                            return updatedItems;
                        });
                    },
                    onFailure: () => {
                        setPending(false);
                    },
                }),
            );
        },
        [
            dispatch,
            search,
            selectedValues,
            fetchAction,
            queryParams,
            compareOptionValue,
            keyProp,
            mapItemToOption,
            setSelectedValues,
        ],
    );

    useEffect(() => {
        if (!items.length && selectedValues?.length && !pending) {
            loadData(1, true);
        }
    }, [selectedValues, loadData, items, pending]);

    // useEffect(() => {
    //     if (hasMore && total > items?.length && currentPage < totalPages) {
    //         loadData(currentPage + 1, false);
    //     }
    // }, [currentPage, items, items?.length, hasMore, loadData, total, totalPages]);

    // useEffect(() => {
    //     if (!initialLoadDone) {
    //         loadData(1, false);
    //     }
    // }, [loadData, initialLoadDone]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (open) {
                if (search) {
                    setWasSearching(true);
                    // Reset items and load search results
                    // loadData(1, true);
                }
            } else {
                setSearch('');
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [search, open, loadData]);

    useEffect(() => {
        if (open) {
            // If we were searching before and now opening without search,
            // we need to reload the full list
            if (wasSearching && !search) {
                setWasSearching(false);
                // loadData(1, false);
            }
        }
    }, [open, wasSearching, search, loadData, currentPage]);

    const handleMenuScroll = (event) => {
        const target = event.target;
        if (target.scrollHeight - target.scrollTop === target.clientHeight && hasMore) {
            loadData(currentPage + 1, false);
        }
    };

    return (
        <CustomAutocomplete
            disabled={disabled}
            multiple
            value={selectedValues || []}
            onChange={(event, value) => {
                handleTagChange(value);
            }}
            onInputChange={(event, newInputValue) => {
                setSearch(newInputValue);
            }}
            ListboxProps={{
                onScroll: handleMenuScroll,
            }}
            popupIcon={<ExpandMoreOutlinedIcon />}
            options={items}
            disableCloseOnSelect
            size={'small'}
            getOptionLabel={(option) => {
                if (getOptionLabel) {
                    return getOptionLabel(option);
                } else {
                    return option[keyProp];
                }
            }}
            isOptionEqualToValue={
                isOptionEqualToValue || ((option, value) => option?.[keyProp || 'id'] === value?.[keyProp || 'id'])
            }
            getOptionDisabled={(option) => {
                if (getOptionDisabled) {
                    return getOptionDisabled(option);
                } else return false;
            }}
            open={open}
            onOpen={() => {
                setOpen(true);
                loadData(1, true);
            }}
            onClose={() => setOpen(false)}
            loading={pending}
            className={classes.root}
            loadingText={t('features.streaming.loading')}
            renderOption={(props, option) => {
                if (renderOption) {
                    return renderOption(props, option);
                } else {
                    return (
                        <li aria-hidden="true" {...props} key={option?.[keyProp] || option.id}>
                            {option[keyProp]}
                        </li>
                    );
                }
            }}
            renderTags={(tagValue, getTagProps) =>
                pending || !items?.length ? (
                    <Typography key={0} align="center" color="textSecondary">
                        {t('features.streaming.loading')}
                    </Typography>
                ) : (
                    tagValue.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                            <Chip
                                key={key}
                                label={getOptionLabel ? getOptionLabel(option) : option[keyProp]}
                                {...tagProps}
                                disabled={getOptionDisabled ? getOptionDisabled(option) : disabled}
                            />
                        );
                    })
                )
            }
            disableClearable={true}
            renderInput={(params) => (
                <TextField
                    disabled
                    {...params}
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <Fragment>
                                {pending ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </Fragment>
                        ),
                    }}
                />
            )}
            autoCorrect="false"
        />
    );
};

export default CustomAutocompleteSearchWithPagination;
